import { load } from 'js-yaml';
import z from 'zod';
import { getDefinitionCheck } from './definitionChecks';

/** Same bound as the settings API. */
const MAX_LENGTH = 65536;

export type CustomDiscoveryDefinitionRow = {
  id: string;
  enabled: boolean;
  yaml: string;
};

const parseYaml = (text: string): unknown => {
  try {
    return load(text);
  } catch {
    return undefined;
  }
};

/** The `name` of the definition, when the text is a YAML mapping that has one. */
export const definitionName = (text: string): string | null => {
  const parsed = parseYaml(text);
  if (typeof parsed !== 'object' || parsed === null || !('name' in parsed)) {
    return null;
  }
  return typeof parsed.name === 'string' && parsed.name.trim() !== ''
    ? parsed.name.trim()
    : null;
};

/** First line of the YAML syntax error, or null when the text is a YAML mapping. */
export const yamlSyntaxError = (text: string): string | null => {
  let parsed: unknown;
  try {
    parsed = load(text);
  } catch (e) {
    return e instanceof Error
      ? (e.message.split('\n')[0] ?? 'Invalid YAML')
      : 'Invalid YAML';
  }
  return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    ? null
    : 'The definition must be a YAML mapping';
};

/**
 * The YAML syntax is checked here, and the server check of the text
 * (`definitionChecks`) blocks an enabled definition that does not load; a
 * disabled one can be saved as a draft. A text not checked yet does not
 * block: saving checks it again on the server.
 */
export const customDiscoveryDefinitionRowSchema = z
  .object({
    id: z.string(),
    enabled: z.boolean(),
    yaml: z
      .string()
      .min(1, 'The definition is empty')
      .max(MAX_LENGTH, `The definition is longer than ${MAX_LENGTH} characters`)
      .superRefine((text, ctx) => {
        const error = yamlSyntaxError(text);
        if (error !== null) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
        }
      }),
  })
  .superRefine((row, ctx) => {
    if (row.enabled && getDefinitionCheck(row.yaml)?.status === 'invalid') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['yaml'],
        message:
          'The definition does not load; fix it or disable it to save the settings',
      });
    }
  });

/** Enabled definitions name the Extension of their rows, so no two may share a name. */
export const customDiscoveryDefinitionsSchema = z
  .array(customDiscoveryDefinitionRowSchema)
  .superRefine((rows, ctx) => {
    const names = new Set<string>();
    rows.forEach((row, i) => {
      const name = row.enabled ? definitionName(row.yaml) : null;
      if (name === null) {
        return;
      }
      if (names.has(name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'yaml'],
          message: `Another enabled definition is named "${name}"`,
        });
      }
      names.add(name);
    });
  });
